/**
 * Hook for the Clinical Note (SOAP/DAP/BIRP) tab: tick/poll generation plus
 * CRUD against /sessions/{sessionId}/notes*. Mirrors api.ts's postSummary
 * tick/poll pattern (per-poll token refresh, 2s interval, throw-on-error).
 */
import { useCallback } from "react";

export type NoteFormat = "soap" | "dap" | "birp";
export type NoteSection = { key: string; title: string; text: string };
export type ProgressNote = {
  session_id: string;
  format: NoteFormat;
  lang: "en" | "hu";
  status: "draft" | "signed";
  sections: NoteSection[];
  model?: string;
  generated_at?: number;
  edited_at?: number;
  signed_at?: number;
};

type UseProgressNotesOptions = {
  apiBase: string;
  ensureToken: () => Promise<string | null>;
  sessionId: string | undefined;
};

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 180_000;

async function parseJsonBody(r: Response): Promise<any> {
  const text = await r.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

export function useProgressNotes({ apiBase, ensureToken, sessionId }: UseProgressNotesOptions) {
  const notesUrl = useCallback(
    (suffix = ""): string => `${apiBase}/sessions/${encodeURIComponent(sessionId as string)}/notes${suffix}`,
    [apiBase, sessionId],
  );

  const loadNotes = useCallback(async (): Promise<ProgressNote[]> => {
    const token = await ensureToken();
    if (!token) throw new Error("No token available");
    const r = await fetch(notesUrl(), {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await parseJsonBody(r);
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return (data.notes || []) as ProgressNote[];
  }, [ensureToken, notesUrl]);

  const generate = useCallback(
    async (format: NoteFormat, lang: "en" | "hu", promptId?: string): Promise<ProgressNote> => {
      const token = await ensureToken();
      if (!token) throw new Error("No token available");

      const sid = sessionId as string;
      const postUrl = `${apiBase}/sessions/${encodeURIComponent(sid)}/notes/generate?session_id=${encodeURIComponent(sid)}`;
      const postResp = await fetch(postUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ format, lang, ...(promptId ? { prompt_id: promptId } : {}) }),
      });
      const postData = await parseJsonBody(postResp);
      if (!postResp.ok) throw new Error(postData.error || `HTTP ${postResp.status}`);
      const runId = postData.run_id;
      if (!runId) throw new Error("No run_id returned");

      const start = Date.now();
      return new Promise<ProgressNote>((resolve, reject) => {
        const iv = setInterval(async () => {
          try {
            if (Date.now() - start > POLL_TIMEOUT_MS) {
              clearInterval(iv);
              reject(new Error("Timeout waiting for note generation"));
              return;
            }

            const freshToken = await ensureToken();
            if (!freshToken) {
              clearInterval(iv);
              reject(new Error("Authentication expired during polling"));
              return;
            }

            const pollUrl = `${apiBase}/sessions/${encodeURIComponent(sid)}/notes/generate?session_id=${encodeURIComponent(sid)}&run_id=${encodeURIComponent(runId)}`;
            const r = await fetch(pollUrl, {
              headers: { Authorization: `Bearer ${freshToken}` },
            });
            const data = await parseJsonBody(r);
            if (!r.ok) {
              clearInterval(iv);
              reject(new Error(data.error || `HTTP ${r.status}`));
              return;
            }

            if (data.status === "done") {
              clearInterval(iv);
              const parsed = typeof data.result === "string" ? JSON.parse(data.result) : data.result;
              resolve(parsed.note as ProgressNote);
            } else if (data.status === "error") {
              clearInterval(iv);
              reject(new Error(data.error || "Worker error"));
            }
          } catch (e) {
            clearInterval(iv);
            reject(e as Error);
          }
        }, POLL_INTERVAL_MS);
      });
    },
    [apiBase, ensureToken, sessionId],
  );

  const saveNote = useCallback(
    async (format: NoteFormat, lang: "en" | "hu", sections: { key: string; text: string }[]): Promise<void> => {
      const token = await ensureToken();
      if (!token) throw new Error("No token available");
      const sid = sessionId as string;
      const r = await fetch(`${apiBase}/sessions/${encodeURIComponent(sid)}/notes/${encodeURIComponent(format)}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ lang, sections }),
      });
      const data = await parseJsonBody(r);
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    },
    [apiBase, ensureToken, sessionId],
  );

  const signNote = useCallback(
    async (format: NoteFormat): Promise<void> => {
      const token = await ensureToken();
      if (!token) throw new Error("No token available");
      const sid = sessionId as string;
      const r = await fetch(`${apiBase}/sessions/${encodeURIComponent(sid)}/notes/${encodeURIComponent(format)}/sign`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await parseJsonBody(r);
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    },
    [apiBase, ensureToken, sessionId],
  );

  const deleteNote = useCallback(
    async (format: NoteFormat): Promise<void> => {
      const token = await ensureToken();
      if (!token) throw new Error("No token available");
      const sid = sessionId as string;
      const r = await fetch(`${apiBase}/sessions/${encodeURIComponent(sid)}/notes/${encodeURIComponent(format)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await parseJsonBody(r);
      if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    },
    [apiBase, ensureToken, sessionId],
  );

  return { loadNotes, generate, saveNote, signNote, deleteNote };
}
